import { Modal, Alert, Upload, message, Tabs } from 'antd';
import { useState, useEffect } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import store from 'store';
import { getImageFallbackUrl, baseURL } from '../../utils/helpers';
import { URLS } from '../../constants';

const { TabPane } = Tabs;
const authToken = store.get('authenticationToken');

const ImageSelectionModal = ({ visible, images, onSelect, onCancel, productId, isCreateMode = false, colorIndex, onImageUploaded }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set());
  const [uploadFileList, setUploadFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [activeTab, setActiveTab] = useState('existing');

  // Clear upload field when modal opens
  useEffect(() => {
    if (visible) {
      // Clear the upload field when modal opens for a new upload
      setUploadFileList([]);
      setUploadError('');
      setSelectedImage(null);
      setActiveTab('existing');
    }
  }, [visible]);


  const handleImageClick = image => {
    setSelectedImage(image); // Store selected image
  };

  const handleConfirm = async () => {
    if (selectedImage) {
      onSelect(selectedImage); // Pass selected image back to parent
    } else if (uploadFileList.length > 0) {
      if (productId) {
        // For existing products: Upload immediately and add to additional images
        await handleUploadAndSelect();
      } else {
        // For new products: Stage the image for later upload but add to additional images immediately
        await handleStageImageForNewProduct();
      }
    }

    // Note: Don't close the modal here - let the parent handle it via onSelect/onCancel
    // The modal will be closed by parent after successful handling
  };


  const handleImageError = (image) => {
    if (!failedImages.has(image.uid)) {
      setFailedImages(prev => new Set([...prev, image.uid]));
    }
  };

  const handleUploadAndSelect = async () => {
    if (!uploadFileList.length || !productId) return;

    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      const uploadedFile = uploadFileList[0];

      if (uploadedFile.originFileObj) {
        formData.append('upload_images', uploadedFile.originFileObj);
      } else {
        throw new Error('No file selected for upload');
      }

      const response = await axios.post(
        `${baseURL}/${URLS.manageProductImages(productId)}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `${authToken}`,
          },
          timeout: 30000,
        }
      );

      if (response.data && response.data.success) {
        // Get the uploaded image URLs from the response
        const uploadedImages = response.data.uploaded_images || [];
        const uploadedImage = uploadedImages[0];

        // Create image object with actual backend URL
        const newImage = {
          uid: uploadedImage?.id || `uploaded-${Date.now()}`,
          name: uploadedFile.name || 'Uploaded Image',
          url: uploadedImage?.image_url || uploadedImage?.url,
          image_url: uploadedImage?.image_url, // Store image_url separately
          status: 'done',
          isNewlyUploaded: true,
          originFileObj: uploadedFile.originFileObj
        };

        // Add to additional images list via callback
        if (onImageUploaded) {
          onImageUploaded(newImage);
        }

        message.success('Image uploaded and added to additional images!');
        onSelect(newImage); // Select the newly uploaded image
      } else {
        const errorMsg = response.data?.message || 'Upload failed';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      let errorMessage = 'Failed to upload image. Please try again.';

      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error;

        if (status === 413) {
          errorMessage = 'Image file is too large. Please choose a smaller image (max 0.75MB).';
        } else if (status === 415) {
          errorMessage = 'Unsupported image format. Please use JPG, PNG, GIF, WEBP, BMP, or SVG.';
        } else if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (status === 404) {
          errorMessage = 'Product not found. Please refresh and try again.';
        } else if (serverMessage) {
          errorMessage = serverMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      message.error(errorMessage);
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleStageImageForNewProduct = async () => {
    if (!uploadFileList.length) return;

    setUploading(true);
    setUploadError('');

    try {
      const uploadedFile = uploadFileList[0];

      if (!uploadedFile.originFileObj) {
        throw new Error('No file selected for staging');
      }

      // Create a preview URL for immediate use
      const previewUrl = URL.createObjectURL(uploadedFile.originFileObj);

      const stagedImage = {
        uid: `staged-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: uploadedFile.name || 'New Image',
        url: previewUrl,
        status: 'done',
        isStaged: true,
        originFileObj: uploadedFile.originFileObj,
        file: uploadedFile
      };

      // Add to additional images list via callback
      if (onImageUploaded) {
        onImageUploaded(stagedImage);
      }

      message.success('Image staged and added to additional images!');
      onSelect(stagedImage); // Select the staged image
    } catch (error) {
      console.error('Error staging image:', error);
      const errorMessage = error.message || 'Failed to stage image. Please try again.';
      message.error(errorMessage);
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Validate file before upload
  const validateFile = (file) => {
    const MAX_SIZE_BYTES = 0.75 * 1024 * 1024; // 0.75 MB
    const hasImageMimeType = file.type && file.type.startsWith('image/');
    const hasImageExtension = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || '');
    const isImage = hasImageMimeType || hasImageExtension;

    if (!isImage) {
      const errorMsg = 'Only image files are allowed (jpg, png, gif, webp, bmp, svg).';
      setUploadError(errorMsg);
      return false;
    }

    if (typeof file.size === 'number' && file.size > MAX_SIZE_BYTES) {
      const errorMsg = `Image must be 0.75 MB or smaller. Current size: ${(file.size / (1024 * 1024)).toFixed(2)} MB`;
      setUploadError(errorMsg);
      return false;
    }

    setUploadError('');
    return true;
  };

  const beforeUpload = (file) => {
    const isValid = validateFile(file);
    if (!isValid) {
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto-upload
  };

  const handleUploadChange = ({ fileList }) => {
    setUploadFileList(fileList);
    // Clear any selected existing image when uploading new one
    if (fileList.length > 0) {
      setSelectedImage(null);
    }
  };

  const handleRemoveUpload = () => {
    setUploadFileList([]);
    setUploadError('');
  };

  // Reset state when modal opens/closes
  const handleModalCancel = () => {
    setSelectedImage(null);
    setUploadFileList([]);
    setUploadError('');
    setActiveTab('existing');
    setUploading(false);
    onCancel();
  };

  const isConfirmDisabled = !selectedImage && uploadFileList.length === 0;
  const confirmButtonText = uploading ? 'Uploading...' : (uploadFileList.length > 0 ? 'Upload & Link' : 'Link Selected');

  return (
    <Modal
      title="Choose or Upload An Image"
      open={visible}
      onOk={handleConfirm}
      onCancel={handleModalCancel}
      okText={confirmButtonText}
      okButtonProps={{
        disabled: isConfirmDisabled || uploading,
        loading: uploading
      }}
      width={700}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Existing Images" key="existing">
          {images.length === 0 ? (
            <Alert
              description={
                <>
                  <p>No additional images available for this product.</p>
                  <p><strong>Tip:</strong> Switch to the "Upload New Image" tab to upload and link a new image directly.</p>
                </>
              }
              type="info"
              showIcon
            />
          ) : (
            <>
              <Alert
                description="Select an existing additional image to link with the color."
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: '8px',
                  height: '40vh',
                  width: '100%',
                  overflow: 'auto',
                }}
              >
                {images.map(image => (
                  <div
                    key={image.uid}
                    style={{
                      width: '7rem',
                      height: '7rem',
                      border: selectedImage?.uid === image.uid ? '3px solid #1890ff' : '1px solid #d9d9d9',
                      cursor: 'pointer',
                      padding: '2px',
                      borderRadius: '6px',
                      transition: 'border-color 0.3s',
                    }}
                    onClick={() => {
                      handleImageClick(image);
                      setUploadFileList([]); // Clear upload when selecting existing
                    }}
                  >
                    <img
                      src={failedImages.has(image.uid) ? getImageFallbackUrl(image.url) : image.url}
                      onError={() => handleImageError(image)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: '4px'
                      }}
                      alt="Product"
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </TabPane>

        <TabPane tab="Upload New Image" key="upload">
          <Alert
            description="Upload a new image and it will be automatically added to the Additional Images section and linked to this color."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <Upload
            listType="picture-card"
            fileList={uploadFileList}
            beforeUpload={beforeUpload}
            onChange={handleUploadChange}
            onRemove={handleRemoveUpload}
            accept="image/*"
            maxCount={1}
            disabled={uploading}
          >
            {uploadFileList.length === 0 && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload Image</div>
              </div>
            )}
          </Upload>

          {uploadError && (
            <div style={{ color: '#ff4d4f', marginTop: 8 }}>{uploadError}</div>
          )}
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default ImageSelectionModal;
